import isMobile from 'ismobilejs'
import debounce from 'lodash/debounce'

export const useUserList = defineStore('userList', () => {
  const { $axios } = useNuxtApp()

  const productStatuses = ref({})
  const productsToFetch = ref(new Set())
  const allLists = ref([])
  const listsLoaded = ref(false)
  const showShareModal = ref(false)
  const sharingList = ref({})

  // --- Toast State ---
  const isToastVisible = ref(false)
  const toastData = reactive({
    notificationStatus: null, // 'created' or 'canceled'
    listsAdded: 0,
    listsRemoved: 0,
  })
  let toastTimeoutId = null

  const fetchProductStatuses = debounce(async () => {
    if (productsToFetch.value.size === 0) return

    const ids = Array.from(productsToFetch.value)
    productsToFetch.value.clear()

    try {
      const response = await $axios.get('/api/customer/lists/product_lists/', {
        params: { products: ids.join(',') },
      })
      productStatuses.value = { ...productStatuses.value, ...response.data }
    } catch (error) {
      console.error('Failed to batch-fetch product statuses:', error)
    }
  }, 100)

  const queueProductStatus = (productId) => {
    if (!productId || productStatuses.value[productId] !== undefined) {
      return
    }
    productsToFetch.value.add(productId)
    fetchProductStatuses()
  }

  const loadAllLists = async (force = false) => {
    if (listsLoaded.value && !force) return
    try {
      const response = await $axios.get('/api/customer/lists/')
      const listsData = response.data.results || response.data
      allLists.value = Array.isArray(listsData) ? listsData : []
      listsLoaded.value = true
    } catch (error) {
      console.error('Failed to fetch user lists:', error)
      allLists.value = []
    }
  }

  const loadProducts = async (userList) => {
    try {
      let next = `/api/customer/list-products/${userList.id}/products/`
      const products = []
      while (next) {
        const result = await $axios.get(next)
        products.push(...result.data.results)
        next = result.data.next
      }
      products.map((x) => (x.product.product_id = x.product.id))
      userList.products = products
    } catch (e) {
      console.error(e)
    }
  }

  const fetchProductStatusImmediately = async (productId) => {
    if (!productId) {
      return
    }
    try {
      const response = await $axios.get('/api/customer/lists/product_lists/', {
        params: { products: productId },
      })

      productStatuses.value[productId] = response.data[productId]
    } catch (error) {
      console.error(`Failed to fetch status for product ${productId}:`, error)

      productStatuses.value[productId] = { notify_me: false, lists: [] }
    }
  }

  const getProductStatus = (productId) => {
    return productStatuses.value[productId]
  }

  // --- Toast Actions ---
  const showToast = (details) => {
    if (toastTimeoutId) {
      clearTimeout(toastTimeoutId)
    }
    toastData.notificationStatus = details.notificationStatus
    toastData.listsAdded = details.listsAdded
    toastData.listsRemoved = details.listsRemoved
    isToastVisible.value = true

    toastTimeoutId = setTimeout(() => {
      hideToast()
    }, 5000)
  }

  const hideToast = () => {
    isToastVisible.value = false
    toastData.notificationStatus = null
    toastData.listsAdded = 0
    toastData.listsRemoved = 0
    toastTimeoutId = null
  }

  const resetLists = () => {
    allLists.value = []
    productStatuses.value = {}
    listsLoaded.value = false
  }

  const shareList = async (userList) => {
    if (!userList.share_link) {
      const link = await $axios.get(
        `/api/customer/lists/${userList.id}/get_share_link/`,
      )
      userList.share_link = link.data.share_url
    }
    if (isMobile().any) {
      window.navigator.share({
        url: userList.share_link,
        title: `${userList.creator} shared a wine list with you - plus $50 off at Wine Access`,
        text: `I've put together a list of wines I think you'll love: ${userList.name}. When you order from Wine Access, you'll get $50 off your first purchase of $150 or more - and trust me, they're by far the best way to discover and enjoy the world's best wines.`,
      })
    } else {
      sharingList.value = userList
      showShareModal.value = true
    }
  }

  return {
    productStatuses,
    getProductStatus,
    queueProductStatus,
    allLists,
    loadAllLists,
    fetchProductStatusImmediately,
    loadProducts,
    isToastVisible,
    toastData,
    showToast,
    hideToast,
    resetLists,
    showShareModal,
    sharingList,
    shareList,
  }
})
